import { useEffect, useMemo, useState } from 'react';

import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, message, Space, Upload } from 'antd';
import {
  DELETE_CONFIRM,
  DELETE_ERROR,
  FETCH_ERROR,
  REQUEST_LOADING,
  SUCCESS_DELETE,
} from 'constants/alerts';

import { useDrawers } from 'hooks/useDrawers';
import { useFilters } from 'hooks/useFilters';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { usePagination } from 'hooks/usePagination';

import { AccessControl } from 'components/AccessControl';
import ClientsSelect from 'components/ClientsSelect';
import { RecordInfoDrawer } from 'components/RecordInfoDrawer';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useQueryParam } from 'hooks/useQueryParam';
import { useTribe } from 'hooks/useTribe';
import { TurnsService } from 'services/TurnsService';
import { ActionsWrapper } from 'ui/ActionsWrapper';
import { formatDate } from 'utils';
import { priceRender } from 'utils/table';
import { TurnsForm } from './TurnsForm';
import { TurnsTable, turnsTypeMap } from './TurnsTable';

const PAGE_TITLE = 'Обороты';

function Turns() {
  useDocumentTitle(PAGE_TITLE);
  const clientId = useQueryParam('client_id');
  const { setCount } = useHeaderChanger(PAGE_TITLE);
  const [turns, setTurns] = useState([]);
  const [loading, setLoading] = useState(false);
  const tribe_id = useTribe();
  const { filters, handleFilters, resetFilters } = useFilters({
    client_id: '',
    start_date: null,
    begin_date: null,
    f_sum: undefined,
    t_sum: undefined,
    type: null,
  });
  const {
    state: { drawerVisible, formIsActive, activeRecord },
    openDrawer,
    closeDrawer,
    openCreateForm,
    openEditForm,
    closeForm,
  } = useDrawers();
  const {
    page,
    totalRows,
    perPage,
    setTotalRows,
    setPagination,
    resetPagination,
  } = usePagination();

  const { client_id: selectedClient } = filters;

  useEffect(() => {
    fetchTurns();
  }, [page, perPage, filters]);

  const turnsRecordFields = useMemo(() => {
    return [
      { label: 'Наименование', key: 'doc_id' },
      {
        label: 'Тип',
        key: 'type',
        computedValue: turnsTypeMap[activeRecord?.type],
      },
      {
        label: 'Сумма',
        key: 'sum',
        computedValue: `${priceRender(activeRecord?.sum)} ₸`,
      },
      {
        label: 'Период',
        key: 'period',
        computedValue: formatDate(activeRecord?.period),
      },
    ];
  }, [activeRecord]);

  const fetchTurns = async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await TurnsService.getTurns(
        page,
        perPage,
        filters,
        tribe_id
      );

      setTurns(items);
      setCount(total);
      setTotalRows(total);
    } catch (err) {
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isDeleted = await TurnsService.deleteTurns(activeRecord.id);

      if (isDeleted) {
        closeDrawer();
        fetchTurns();
        message.success(SUCCESS_DELETE);
      }
    } catch (err) {
      message.error(DELETE_ERROR);
    } finally {
      hide();
    }
  };

  const uploadTurns = async (data) => {
    const exportFile = data?.file;

    if (!exportFile) {
      message.warning('Загрузите excel файл');
      return;
    }

    const formData = new FormData();
    formData.append('file', exportFile);

    const hide = message.loading(REQUEST_LOADING);

    try {
      const isUploaded = await TurnsService.uploadTruns(
        selectedClient,
        formData
      );

      if (isUploaded) {
        message.success('Импорт оборотов успешно');
      }
    } catch (err) {
      message.error('Не удалось загрузить импорт оборотов');
    } finally {
      hide();
    }
  };

  const handleClientSelect = (id = '') => {
    resetFilters();
    handleFilters({ client_id: id });
    resetPagination();
  };

  return (
    <>
      <ActionsWrapper>
        <ClientsSelect
          clientId={clientId}
          pageTitle={PAGE_TITLE}
          onSelect={handleClientSelect}
          onClear={handleClientSelect}
        />

        <AccessControl pageId="circulations" action="create">
          <Space>
            <Button
              disabled={!selectedClient}
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateForm}
            >
              Создать
            </Button>

            <Upload
              name="file"
              maxCount={1}
              accept=".xlsx"
              beforeUpload={() => false}
              onChange={uploadTurns}
              showUploadList={false}
            >
              <Button disabled={!selectedClient} icon={<UploadOutlined />}>
                Импорт оборотов
              </Button>
            </Upload>
          </Space>
        </AccessControl>
      </ActionsWrapper>

      <TurnsTable
        items={turns}
        loading={loading}
        current={page}
        pageSize={perPage}
        totalRows={totalRows}
        filters={filters}
        onRowClick={openDrawer}
        onChange={setPagination}
        onFilter={(value) => {
          handleFilters(value);
          resetPagination();
        }}
        onResetFilter={resetFilters}
      />

      <RecordInfoDrawer
        record={activeRecord}
        drawerTitle={activeRecord?.company_name}
        pageId="circulations"
        recordFields={turnsRecordFields}
        isVisible={drawerVisible}
        onClose={closeDrawer}
        onEdit={openEditForm}
        onDelete={handleDelete}
        deleteConfirmText={DELETE_CONFIRM}
      />

      <TurnsForm
        clientId={selectedClient}
        isActive={formIsActive}
        initialValues={activeRecord}
        onSuccess={fetchTurns}
        onClose={closeForm}
      />
    </>
  );
}

export { Turns };
